import React, { useState, useEffect } from "react";
import {
  Button,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import { Drawer, message, Flex, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import Select from "react-select";
import rolesApi from "../../api/master/roles";
import Swal from "sweetalert2";

interface Employee {
  name: string;
  id: string;
}

interface Role {
  roleName: string;
  employees: Employee[];
  roleID: any;
  level: string;
}

interface ApprovalData {
  roleId: any;
  level: string;
  userId: any;
  approvalStage?: string;
}

interface ExpenseApprovalDrawerProps {
  open: boolean;
  siteId: number | (() => Promise<number>);
  onClose: () => void;
  onSubmit: (data: ApprovalData[]) => void;
  setApprovalSubmissionStatus: (status: boolean) => void;
  approvalData?: ApprovalData[];
}

const ExpenseApprovalDrawer: React.FC<ExpenseApprovalDrawerProps> = ({
  open,
  siteId,
  onClose,
  onSubmit,
  setApprovalSubmissionStatus,
  approvalData = []
}) => {
  const [approvalRoles, setApprovalRoles] = useState<Role[]>([]);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [formData, setFormData] = useState<{ [key: string]: any }>({});

  // Existing Data Handling: Initialize form data with existing approval data
  useEffect(() => {
    if (approvalData && approvalData.length > 0) {
      const initialFormData: { [key: string]: any } = {};
      approvalData.forEach((approval) => {
        initialFormData[approval.level] = approval.userId;
        initialFormData[`approvalStage_${approval.level}`] = approval.approvalStage;
      });
      setFormData(initialFormData);
    } else {
      setFormData({});
    }
  }, [approvalData]);

  // Module Integration: Fetch approval hierarchy users by module ID
  const fetchApprovalHierarchyUsersByModuleId = async (
    moduleID: number,
    siteId: number
  ) => {
    try {
      setApprovalLoading(true);
      const res = await rolesApi.fetchHierarchyUsersByModuleId(
        moduleID,
        siteId
      );
      if (!res) return;
      const { data, status } = res;
      if (status) {
        setApprovalRoles(data);
      }
    } catch (error) {
    } finally {
      setApprovalLoading(false);
    }
  };

  // Module Integration: Fetch module configuration and approval roles
  const fetchModuleConfig = async () => {
    try {
      const res = await rolesApi.getModuleConfig();
      const moduleID = 134; // ID for expenses approvals

      let siteIdToUse = 1;
      if (typeof siteId === 'function') {
        siteIdToUse = await siteId();
      } else {
        siteIdToUse = siteId || 1;
      }

      await fetchApprovalHierarchyUsersByModuleId(moduleID, siteIdToUse);
    } catch (err) {
    }
  };

  // Dynamic Loading: Load approval roles when drawer opens
  useEffect(() => {
    if (open) {
      fetchModuleConfig();
    }
  }, [open]);

  // Form Management: Handle form field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Form Validation: Handle form submission with validation
  const handleSubmit = async () => {
    const requiredFields = approvalRoles.flatMap(role => [
      role.level,
      `approvalStage_${role.level}`
    ]);

    const missingFields = requiredFields.filter(field => !formData[field]);

    if (missingFields.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please fill in all required fields",
        confirmButtonText: "OK",
      });
      return; // Keep drawer open on validation error
    }

    const hasApprover = approvalRoles.some(role =>
      formData[`approvalStage_${role.level}`] === "Approver"
    );

    if (!hasApprover) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "At least one approval level must be set as 'Approver'",
        confirmButtonText: "OK",
      });
      return; // Keep drawer open on validation error
    }

    const approvalData: ApprovalData[] = approvalRoles.map((role) => {
      const userId = formData[role.level];
      const approvalStage = formData[`approvalStage_${role.level}`];

      return {
        roleId: Number(role.roleID),
        level: role.level,
        userId: userId,
        approvalStage: approvalStage,
      };
    });

    try {
      // Call onSubmit and wait for it to complete
      await onSubmit(approvalData);
      
      // If onSubmit succeeds, show success message and close drawer
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Approval settings saved successfully",
        confirmButtonText: "OK",
      });
      
      setApprovalSubmissionStatus(true);
      onClose(); // Close drawer only on success
    } catch (error) {
      // If onSubmit fails, show error message but keep drawer open
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save approval settings. Please try again.",
        confirmButtonText: "OK",
      });
      // Don't close drawer on error - keep it open for user to retry
    }
  };

  return (
    <Drawer
      title="Set Report Approvals"
      placement="right"
      onClose={onClose}
      open={open}
      width={"500px"}
    >
      {approvalLoading ? (
        <Flex align="center" gap="middle" className="pageLoader">
          <Spin
            indicator={<LoadingOutlined spin />}
            className="m-auto"
            size="large"
          />
        </Flex>
      ) : (
        <div>
          <p>
            There are {approvalRoles.length} levels of approvals set for this process
          </p>
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            {approvalRoles.map((role, index) => (
              <Row className="mt-2" key={index}>
                <Col lg="6">
                  <label>{`${role.roleName}`}</label>
                  <Select
                    classNamePrefix="react-select"
                    options={role.employees?.map((employee) => ({
                      value: employee.id,
                      label: employee.name,
                    }))}
                    value={formData[role.level] ? {
                      value: formData[role.level],
                      label: role.employees?.find(emp => emp.id === formData[role.level])?.name || formData[role.level]
                    } : null}
                    onChange={(selectedOption) => {
                      handleFieldChange(role.level, selectedOption?.value || '');
                    }}
                    placeholder={`Select ${role.roleName}`}
                    isClearable
                    className="react-select-container"
                    menuPlacement="auto"
                  />
                </Col>
                <Col lg="6">
                  <label>{`Approval Stage`}</label>
                  <Select
                    classNamePrefix="react-select"
                    options={[
                      { value: "Verifier", label: "Verifier" },
                      { value: "Checker", label: "Checker" },
                      { value: "Approver", label: "Approver" },
                    ]}
                    value={formData[`approvalStage_${role.level}`] ? {
                      value: formData[`approvalStage_${role.level}`],
                      label: formData[`approvalStage_${role.level}`]
                    } : null}
                    onChange={(selectedOption) => {
                      handleFieldChange(`approvalStage_${role.level}`, selectedOption?.value || '');
                    }}
                    placeholder={`Select Approval Stage`}
                    isClearable
                    className="react-select-container"
                    menuPlacement="auto"
                  />
                </Col>
              </Row>
            ))}
            <Button type="submit" color="primary" className="mt-3">
              Save
            </Button>
          </form>
        </div>
      )}
    </Drawer>
  );
};

export default ExpenseApprovalDrawer;